import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, MapPin, Calendar } from "lucide-react";

const AnnouncementsPage = ({ user, onLogout }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  useEffect(() => {
    fetchAnnouncements();
  }, [selectedCity, selectedType]);

  const fetchAnnouncements = async () => {
    try {
      const params = {};
      if (selectedCity !== "all") params.city = selectedCity;
      if (selectedType !== "all") params.type = selectedType;
      
      const response = await axios.get(`${API}/announcements`, { params });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error("Duyurular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      discount: "İndirim",
      tournament: "Turnuva",
      maintenance: "Bakım",
      general: "Genel"
    };
    return labels[type] || type;
  };

  const getTypeBadge = (type) => {
    const colors = {
      discount: "bg-green-100 text-green-800",
      tournament: "bg-purple-100 text-purple-800",
      maintenance: "bg-orange-100 text-orange-800",
      general: "bg-blue-100 text-blue-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-6">
            Duyurular
          </h1>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Şehirler</SelectItem>
                      <SelectItem value="İstanbul">İstanbul</SelectItem>
                      <SelectItem value="Ankara">Ankara</SelectItem>
                      <SelectItem value="İzmir">İzmir</SelectItem>
                      <SelectItem value="Bursa">Bursa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Tipler</SelectItem>
                      <SelectItem value="general">Genel</SelectItem>
                      <SelectItem value="discount">İndirim</SelectItem>
                      <SelectItem value="tournament">Turnuva</SelectItem>
                      <SelectItem value="maintenance">Bakım</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Announcements */}
          {announcements.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center text-gray-500">
                  <Megaphone className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Duyuru bulunamadı</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {announcements.map((ann) => (
                <Card key={ann.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-50 rounded-full">
                        <Megaphone className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-bold">{ann.title}</h3>
                            <p className="text-sm text-gray-600">{ann.venueName}</p>
                          </div>
                          <Badge className={getTypeBadge(ann.type)}>
                            {getTypeLabel(ann.type)}
                          </Badge>
                        </div>
                        <p className="text-gray-700 mb-3">{ann.message}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          {ann.city && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {ann.district && `${ann.district}, `}{ann.city}
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(ann.createdAt).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AnnouncementsPage;
